import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        this.setState({ errorInfo });
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="min-h-screen bg-dark-950 flex items-center justify-center p-6">
                    <div className="max-w-md w-full text-center">
                        <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                            <AlertTriangle className="w-8 h-8 text-red-400" />
                        </div>
                        <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
                        <p className="text-dark-400 mb-6">
                            We encountered an unexpected error. Please try again or return to the home page.
                        </p>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <div className="mb-6 p-4 bg-dark-800 rounded-lg text-left overflow-auto max-h-48">
                                <p className="text-red-400 text-sm font-mono">
                                    {this.state.error.toString()}
                                </p>
                                {this.state.errorInfo?.componentStack && (
                                    <pre className="text-dark-500 text-xs mt-2 whitespace-pre-wrap">
                                        {this.state.errorInfo.componentStack}
                                    </pre>
                                )}
                            </div>
                        )}

                        <div className="flex gap-4 justify-center">
                            <button
                                onClick={this.handleRetry}
                                className="btn btn-secondary flex items-center gap-2"
                            >
                                <RefreshCw className="w-4 h-4" />
                                Try Again
                            </button>
                            <button
                                onClick={this.handleGoHome}
                                className="btn btn-primary flex items-center gap-2"
                            >
                                <Home className="w-4 h-4" />
                                Go Home
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Hook-based error display for API errors
interface ApiErrorProps {
    error: Error | null;
    onRetry?: () => void;
    message?: string;
}

export function ApiError({ error, onRetry, message }: ApiErrorProps) {
    if (!error) return null;

    return (
        <div className="card border-red-500/20 bg-red-500/5">
            <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-red-400">
                        {message || 'Failed to load data'}
                    </h3>
                    <p className="text-dark-400 text-sm mt-1">
                        {error.message || 'An unexpected error occurred. Please try again.'}
                    </p>
                    {onRetry && (
                        <button
                            onClick={onRetry}
                            className="mt-3 text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Retry
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// Loading skeleton components
export function StatsSkeleton() {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
                <div key={i} className="card animate-pulse">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-dark-700 rounded-xl" />
                        <div className="flex-1">
                            <div className="h-6 bg-dark-700 rounded w-16 mb-2" />
                            <div className="h-4 bg-dark-700 rounded w-24" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export function ProjectsSkeleton() {
    return (
        <div className="space-y-4">
            {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between p-4 rounded-lg bg-dark-800/50 animate-pulse">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-dark-700 rounded-lg" />
                        <div>
                            <div className="h-5 bg-dark-700 rounded w-32 mb-2" />
                            <div className="h-4 bg-dark-700 rounded w-24" />
                        </div>
                    </div>
                    <div className="h-6 bg-dark-700 rounded w-16" />
                </div>
            ))}
        </div>
    );
}

export function HealthSkeleton() {
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="flex items-center gap-2 p-3 rounded-lg bg-dark-800/50 animate-pulse">
                    <div className="w-5 h-5 bg-dark-700 rounded-full" />
                    <div className="h-4 bg-dark-700 rounded w-16" />
                </div>
            ))}
        </div>
    );
}

export default ErrorBoundary;
